import React, { useMemo } from "react";
import { Button, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { ShootingSession } from "../types/ShootingSession";
import { TargetResultDto } from "../types/TargetScannedDataDTOs";
import { getShootingEventConfig } from "../configs/utils/ShootingEventConfigFactory";

export interface RankedEntry {
    session: ShootingSession;
    totalPoints: number;
    scoreCounts: Map<string, number>;
    place: number;
}

export function normalizeScoreKey(score: string): string {
    if (score.toLowerCase() === "10x") {
        return "10X";
    }
    return score;
}

export function parsePointValue(point: string): number {
    return parseFloat(point);
}

export function buildScoreCounts(targetResults: TargetResultDto[], scoreOrder: string[]): Map<string, number> {
    const counts = new Map<string, number>();
    for (const score of scoreOrder) {
        counts.set(score, 0);
    }
    for (const tr of targetResults) {
        for (const point of tr.listOfPointsScored) {
            const normalizedPoint = normalizeScoreKey(point);
            counts.set(normalizedPoint, (counts.get(normalizedPoint) ?? 0) + 1);
        }
    }
    return counts;
}

export function compareEntries(a: RankedEntry, b: RankedEntry, scoreOrder: string[]): number {
    if (b.totalPoints !== a.totalPoints) return b.totalPoints - a.totalPoints;
    for (const score of scoreOrder) {
        const countA = a.scoreCounts.get(score) ?? 0;
        const countB = b.scoreCounts.get(score) ?? 0;
        if (countB !== countA) return countB - countA;
    }
    return 0;
}

export function getScoreOrderForEvent(shootingEventId: string): string[] {
    try {
        const shootingEventConfig = getShootingEventConfig(shootingEventId);
        return Object.values(shootingEventConfig.targetConfig.fieldStartRadiusToScoreMap as Record<string, string>);
    } catch (error) {
        console.error("Error reading score order from config:", error);
        return [];
    }
}

interface EventRankingTableProps {
    sessions: ShootingSession[];
    shootingEventId: string;
    competitionName?: string;
    competitionDate?: string;
    showLastModified?: boolean;
    onDeleteSession?: (session: ShootingSession) => void;
    maxEntries?: number;
}

function normalizeUtcDateString(dateString: string): string {
    const hasTimezoneInfo = /[zZ]|[+-]\d{2}:\d{2}$/.test(dateString);
    return hasTimezoneInfo ? dateString : `${dateString}Z`;
}

function formatDateTime(dateString: string): string {
    const date = new Date(normalizeUtcDateString(dateString));
    return new Intl.DateTimeFormat('pl-PL', {
        timeZone: 'Europe/Warsaw',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    }).format(date).replace(',', '');
}

function EventRankingTable({ sessions, shootingEventId, competitionName, competitionDate, showLastModified = false, onDeleteSession, maxEntries }: EventRankingTableProps) {
    const navigate = useNavigate();

    const handleRowClick = (session: ShootingSession) => {
        navigate(`/session-reports/${session.id}`, {
            state: {
                targetOwnerName: session.owner,
                competitionName,
                competitionDate,
            },
        });
    };

    const scoreOrder = useMemo(() => getScoreOrderForEvent(shootingEventId), [shootingEventId]);

    const extraScoreColumns = useMemo(() => {
        const specialTenColumns = ["10X", "10/"].filter(score => scoreOrder.includes(score));
        if (specialTenColumns.length > 0) {
            return specialTenColumns;
        }
        return scoreOrder.includes("10") ? ["10"] : [];
    }, [scoreOrder]);

    const rankedEntries = useMemo(() => {
        const entries: RankedEntry[] = sessions.map(session => {
            const allPoints = session.targetResults.flatMap(tr => tr.listOfPointsScored);
            const totalPoints = allPoints.reduce((sum, p) => sum + parsePointValue(p), 0);
            const scoreCounts = buildScoreCounts(session.targetResults, scoreOrder);
            return { session, totalPoints, scoreCounts, place: 0 };
        });

        entries.sort((a, b) => compareEntries(a, b, scoreOrder));

        let currentPlace = 1;
        entries.forEach((entry, index) => {
            if (index > 0 && compareEntries(entries[index - 1], entry, scoreOrder) !== 0) {
                currentPlace = index + 1;
            }
            entry.place = currentPlace;
        });

        return maxEntries !== undefined ? entries.slice(0, maxEntries) : entries;
    }, [sessions, scoreOrder, maxEntries]);

    if (rankedEntries.length === 0) return null;

    return (
        <Table bordered hover responsive className="text-center align-middle event-ranking-table">
            <thead className="table-dark">
                <tr>
                    <th>Miejsce</th>
                    <th>Nazwisko i imię</th>
                    <th>Klub</th>
                    <th>Liczba punktów</th>
                    {extraScoreColumns.map(score => (
                        <th key={score}>{score}</th>
                    ))}
                    {showLastModified && <th>Data modyfikacji</th>}
                    {onDeleteSession && <th>Akcja</th>}
                </tr>
            </thead>
            <tbody>
                {rankedEntries.map((entry) => (
                    <tr
                        key={entry.session.id}
                        style={{ cursor: "pointer" }}
                        onClick={() => handleRowClick(entry.session)}
                    >
                        <td>{entry.place}</td>
                        <td>{entry.session.owner}</td>
                        <td>{entry.session.club}</td>
                        <td>{entry.totalPoints}</td>
                        {extraScoreColumns.map(score => (
                            <td key={score}>{entry.scoreCounts.get(score) ?? 0}</td>
                        ))}
                        {showLastModified && <td>{formatDateTime(entry.session.lastModified)}</td>}
                        {onDeleteSession && (
                            <td>
                                <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onDeleteSession(entry.session);
                                    }}
                                >
                                    Usuń
                                </Button>
                            </td>
                        )}
                    </tr>
                ))}
            </tbody>
        </Table>
    );
}

export default EventRankingTable;
